/**
 * Marketing — disparo de e-mail/SMS em lote (dono 2026-09-30). Mesmo nível GM do menu Notícias.
 *
 * Lista de gatilhos (abortar / reenviar) + "Novo Gatilho" (modal em 2 passos):
 *   1. canal (SMS|E-mail) + público (não loga desde X | loga desde X) -> Pesquisar -> lote MASCARADO
 *   2. mensagem: SMS (limite de 1 SMS) | e-mail HTML ou "E-mail padrão" (texto no layout Liberty) -> Disparar
 *
 * O envio é feito pela API devagar (ritmo fixo por canal) — ver app/marketing/service.py.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Megaphone, Loader2, AlertCircle, Plus, X, Mail, MessageSquare, Search, Eye, Ban, RotateCcw, ArrowLeft, Send,
} from 'lucide-react';
import {
  getMarketingConfig, previewMarketing, listMarketing, getMarketing, createMarketing, abortMarketing,
  resendMarketing,
  type MarketingConfig, type MarketingCampaign, type MarketingChannel, type MarketingAudience,
  type MarketingPreview,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

const errMsg = (err: any) => err?.detail || err?.message || 'Erro';
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '—');
const fmtDay = (s: string) => new Date(s).toLocaleDateString('pt-BR');
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const audienceLabel = (a: MarketingAudience, since: string, hours = 0, recharge = 0) =>
  (a === 'inactive' ? `Não loga desde ${fmtDay(since)}` : `Loga desde ${fmtDay(since)}`)
  + (hours > 0 ? ` · jogou ${hours}h+` : '')
  + (recharge > 0 ? ` · recarregou ${brl(recharge)}+` : '');

const STATUS: Record<MarketingCampaign['status'], { label: string; cls: string }> = {
  running: { label: 'Enviando', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  aborted: { label: 'Abortado', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  done:    { label: 'Concluído', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
};

// Mesmo cálculo da API (app/marketing/service.py sms_limit): GSM-7 = 160, senão (acento/emoji) = 70.
const GSM7 = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡' +
  'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà');
const GSM7_EXT = new Set('^{}\\[~]|€');
function smsLimit(text: string): { used: number; limit: number } {
  const chars = Array.from(text);
  if (chars.every((c) => GSM7.has(c) || GSM7_EXT.has(c)))
    return { used: chars.reduce((n, c) => n + (GSM7_EXT.has(c) ? 2 : 1), 0), limit: 160 };
  return { used: text.length, limit: 70 };   // UTF-16 code units = o que o UCS-2 conta
}

export default function MarketingPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<MarketingCampaign[]>([]);
  const [cfg, setCfg] = useState<MarketingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<MarketingCampaign | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try { setItems(await listMarketing()); }
    catch (err) { setError(errMsg(err)); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    getMarketingConfig().then(setCfg).catch(() => setCfg(null));
  }, [fetchData]);

  // enquanto houver gatilho enviando, atualiza o progresso sozinho
  const anyRunning = items.some((c) => c.status === 'running');
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(t);
  }, [anyRunning, fetchData]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); notify('success', ok); await fetchData(true); }
    catch (err) { notify('error', errMsg(err)); }
  };
  const abort = (c: MarketingCampaign) => {
    if (!confirm(`Abortar o gatilho #${c.campaignId}? O que já foi enviado não volta.`)) return;
    act(() => abortMarketing(c.campaignId), 'Gatilho abortado');
  };
  const resend = (c: MarketingCampaign) => {
    if (!confirm(`Retomar o gatilho #${c.campaignId}? Pendentes e falhas voltam pra fila (quem já recebeu não recebe de novo).`)) return;
    act(() => resendMarketing(c.campaignId), 'Gatilho retomado');
  };
  const view = async (c: MarketingCampaign) => {
    try { setViewing(await getMarketing(c.campaignId)); }
    catch (err) { notify('error', errMsg(err)); }
  };

  const actionsFor = (c: MarketingCampaign) => [
    { label: 'Ver mensagem', icon: <Eye size={13} />, onClick: () => view(c) },
    ...(c.status === 'running'
      ? [{ label: 'Abortar', icon: <Ban size={13} />, onClick: () => abort(c), variant: 'danger' as const }]
      : []),
    ...(c.status !== 'running' && (c.failed > 0 || c.pending > 0)
      ? [{ label: 'Reenviar falhas/pendentes', icon: <RotateCcw size={13} />, onClick: () => resend(c), variant: 'warning' as const }]
      : []),
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Megaphone className="text-amber-400" size={26} />
            Marketing
          </h1>
          <p className="text-sm text-white/65 mt-1">
            Disparo de e-mail e SMS em lote · envio lento e seguro
            {cfg && ` (e-mail ${cfg.emailPerMin}/min · SMS ${cfg.smsPerMin}/min)`}
          </p>
          {cfg?.emailRemain != null && (
            <p className="text-[12px] text-white/55 mt-1">
              E-mails disponíveis no mês:{' '}
              <strong className="text-amber-300">{cfg.emailRemain.toLocaleString('pt-BR')}</strong>
              {cfg.emailTotal ? ` de ${cfg.emailTotal.toLocaleString('pt-BR')}` : ''}
              {cfg.emailNextReset && ` · renova em ${new Date(cfg.emailNextReset + 'T00:00:00').toLocaleDateString('pt-BR')}`}
              {cfg.emailReputation != null && ` · reputação ${cfg.emailReputation}%`}
            </p>
          )}
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold btn-primary transition cursor-pointer"
        >
          <Plus size={14} />
          Novo Gatilho
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<MarketingCampaign>
        columns={[
          { key: 'campaign', label: 'Gatilho', width: '1.4fr',
            cell: (c) => (
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate flex items-center gap-2">
                  {c.channel === 'email' ? <Mail size={13} className="text-amber-300 shrink-0" />
                                         : <MessageSquare size={13} className="text-sky-300 shrink-0" />}
                  #{c.campaignId} · {c.channel === 'email' ? (c.subject || '(sem assunto)') : 'SMS'}
                </p>
                <p className="text-[11px] text-white/55 mt-0.5 truncate">
                  {audienceLabel(c.audience, c.sinceDate, c.minPlayHours, c.minRechargeBrl)} · por {c.createdByName || '?'} · {fmtDate(c.createdAt)}
                </p>
              </div>
            ) },
          { key: 'progress', label: 'Progresso', width: '1fr',
            cell: (c) => {
              const done = c.sent + c.failed + c.skipped;
              const pct = c.total ? Math.round((done * 100) / c.total) : 0;
              return (
                <div className="min-w-0">
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full bg-amber-400/80" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-white/60 mt-1 font-mono">
                    {c.sent}/{c.total} enviados
                    {c.failed > 0 && <span className="text-red-300"> · {c.failed} falhas</span>}
                    {c.skipped > 0 && <span className="text-white/45"> · {c.skipped} descadastrados</span>}
                  </p>
                  {c.lastError && c.failed > 0 && (
                    <p className="text-[10px] text-red-300/80 truncate" title={c.lastError}>{c.lastError}</p>
                  )}
                </div>
              );
            } },
          { key: 'status', label: 'Status', width: '110px',
            cell: (c) => (
              <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-semibold ${STATUS[c.status].cls}`}>
                {STATUS[c.status].label}
              </span>
            ) },
          { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
            cell: (c) => <RowActions actions={actionsFor(c)} /> },
        ]}
        rows={items}
        rowKey={(c) => c.campaignId}
        loading={loading}
        empty="Nenhum gatilho ainda"
        mobileTitle={(c) => `#${c.campaignId} · ${c.channel === 'email' ? (c.subject || 'E-mail') : 'SMS'}`}
        mobileSubtitle={(c) => `${STATUS[c.status].label} · ${c.sent}/${c.total}`}
        mobileActions={(c) => <RowActions actions={actionsFor(c)} align="left" />}
      />

      {creating && (
        <NewTrigger
          cfg={cfg}
          onClose={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await fetchData(); }}
        />
      )}
      {viewing && <ViewMessage c={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// ----------------------------------------------------------------- modal: novo gatilho

function NewTrigger({
  cfg, onClose, onCreated,
}: { cfg: MarketingConfig | null; onClose: () => void; onCreated: () => Promise<void> }) {
  const { notify } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [channel, setChannel] = useState<MarketingChannel>('email');
  const [audience, setAudience] = useState<MarketingAudience>('inactive');
  const [since, setSince] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [minHours, setMinHours] = useState(0);
  const [minRecharge, setMinRecharge] = useState(0);
  const [preview, setPreview] = useState<MarketingPreview | null>(null);
  const [searching, setSearching] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [useTemplate, setUseTemplate] = useState(true);
  const [sending, setSending] = useState(false);

  const enabled = (ch: MarketingChannel) => !cfg || (ch === 'email' ? cfg.emailEnabled : cfg.smsEnabled);
  const sinceIso = `${since}T00:00:00`;
  const sms = useMemo(() => smsLimit(body), [body]);

  // mudou o filtro -> o lote pesquisado não vale mais
  useEffect(() => { setPreview(null); }, [channel, audience, since, minHours, minRecharge]);
  const filters = { minPlayHours: minHours, minRechargeBrl: minRecharge };

  const search = async () => {
    setSearching(true);
    try { setPreview(await previewMarketing({ channel, audience, sinceDate: sinceIso, ...filters })); }
    catch (err) { notify('error', errMsg(err)); }
    finally { setSearching(false); }
  };

  const fire = async () => {
    if (!preview) return;
    if (!body.trim()) { notify('error', 'Escreva a mensagem'); return; }
    if (channel === 'email' && subject.trim().length < 3) { notify('error', 'Assunto obrigatório'); return; }
    if (channel === 'sms' && sms.used > sms.limit) { notify('error', `SMS passou do limite (${sms.limit})`); return; }
    if (!confirm(`Disparar para ${preview.total.toLocaleString('pt-BR')} destinatário(s)? `
                 + `Leva ~${preview.etaMinutes} min. Não dá pra desfazer o que já saiu.`)) return;
    setSending(true);
    try {
      await createMarketing({
        channel, audience, sinceDate: sinceIso, ...filters, body,
        subject: channel === 'email' ? subject : undefined,
        useTemplate: channel === 'email' && useTemplate,
      });
      notify('success', 'Gatilho disparado — o envio segue em segundo plano');
      await onCreated();
    } catch (err) {
      notify('error', errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">
            Novo Gatilho <span className="text-white/45 text-sm font-normal">· passo {step} de 2</span>
          </h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Canal</label>
              <div className="grid grid-cols-2 gap-2">
                {(['email', 'sms'] as const).map((ch) => (
                  <button key={ch} type="button" disabled={!enabled(ch)} onClick={() => setChannel(ch)}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition
                      ${channel === ch ? 'border-amber-400/60 bg-amber-400/10 text-amber-200'
                                       : 'border-white/[0.08] bg-white/[0.03] text-white/70 hover:bg-white/[0.06]'}
                      disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer`}>
                    {ch === 'email' ? <Mail size={15} /> : <MessageSquare size={15} />}
                    {ch === 'email' ? 'E-mail' : 'SMS'}
                    {!enabled(ch) && <span className="text-[10px] font-normal">(não configurado)</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Público</label>
                <select value={audience} onChange={(e) => setAudience(e.target.value as MarketingAudience)}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
                  <option value="inactive">Quem NÃO loga desde</option>
                  <option value="active">Quem loga desde</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Data</label>
                <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Jogou pelo menos (horas) · 0 = todos</label>
                <input type="number" min={0} step={1} value={minHours}
                  onChange={(e) => setMinHours(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Já recarregou pelo menos (R$) · 0 = todos</label>
                <input type="number" min={0} step={1} value={minRecharge}
                  onChange={(e) => setMinRecharge(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>

            <p className="text-[11px] text-white/45 leading-relaxed">
              Sempre fora do lote: contas banidas, bots, staff (GMs), quem se descadastrou e destinos repetidos.
              {channel === 'email' && ' E-mail só de provedores conhecidos (Gmail, Hotmail, Outlook, Yahoo, iCloud, UOL, BOL…).'}
            </p>

            <button onClick={search} disabled={searching || !enabled(channel)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.06]
                         hover:bg-white/[0.1] text-white text-sm font-semibold transition cursor-pointer disabled:opacity-50">
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Pesquisar
            </button>

            {preview && (
              <div className="rounded-xl border border-white/[0.06] bg-[#0b1017]">
                <div className="px-4 py-3 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-white">
                    <strong className="text-amber-300">{preview.total.toLocaleString('pt-BR')}</strong> destinatário(s)
                  </p>
                  <p className="text-[11px] text-white/55">envio estimado: ~{preview.etaMinutes} min</p>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-white/[0.04]">
                  {preview.rows.map((r) => (
                    <div key={r.userNum} className="px-4 py-1.5 flex justify-between text-[12px] font-mono">
                      <span className="text-white/45">#{r.userNum}</span>
                      <span className="text-white/75">{r.destination}</span>
                    </div>
                  ))}
                  {preview.total > preview.rows.length && (
                    <p className="px-4 py-2 text-[11px] text-white/45">
                      … e mais {(preview.total - preview.rows.length).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
                Cancelar
              </button>
              <button onClick={() => setStep(2)} disabled={!preview || preview.total === 0}
                className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-[#0d141b] font-bold text-sm
                           transition cursor-pointer disabled:opacity-50">
                Confirmar lote
              </button>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <p className="text-[12px] text-white/60">
              {channel === 'email' ? 'E-mail' : 'SMS'} · {audienceLabel(audience, sinceIso, minHours, minRecharge)} ·{' '}
              <strong className="text-amber-300">{preview.total.toLocaleString('pt-BR')}</strong> destinatário(s)
            </p>

            {channel === 'sms' ? (
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Mensagem (1 SMS)</label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white resize-y" />
                <p className={`text-[11px] mt-1 ${sms.used > sms.limit ? 'text-red-400' : 'text-white/50'}`}>
                  {sms.used}/{sms.limit} caracteres
                  {sms.limit === 70 && ' · acento ou emoji reduz o limite pra 70'}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-[11px] text-white/55 mb-1 block">Assunto</label>
                  <input type="text" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)}
                    className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
                </div>
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
                  <input type="checkbox" checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} />
                  E-MAIL PADRÃO <span className="text-[11px] text-white/45">(texto no layout da Liberty)</span>
                </label>
                <div>
                  <label className="text-[11px] text-white/55 mb-1 block">
                    {useTemplate ? 'Texto (linha em branco separa parágrafos)' : 'HTML'}
                  </label>
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={useTemplate ? 8 : 12}
                    className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white resize-y
                                ${useTemplate ? '' : 'font-mono'}`} />
                </div>
                {!useTemplate && body.trim() && (
                  <div>
                    <label className="text-[11px] text-white/55 mb-1 block">Pré-visualização</label>
                    <iframe title="preview" sandbox="" srcDoc={body}
                      className="w-full h-64 rounded-lg border border-white/[0.06] bg-white" />
                  </div>
                )}
                <p className="text-[11px] text-white/45">
                  Todo e-mail sai com o link “Não quero mais receber” no rodapé — quem sair nunca mais entra em lote.
                </p>
              </>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={() => setStep(1)}
                className="px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm
                           flex items-center gap-1.5">
                <ArrowLeft size={14} /> Voltar
              </button>
              <button onClick={fire} disabled={sending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-[#0d141b] font-bold text-sm
                           transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Disparar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- modal: ver mensagem

function ViewMessage({ c, onClose }: { c: MarketingCampaign; onClose: () => void }) {
  const isHtml = c.channel === 'email' && !c.useTemplate;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Gatilho #{c.campaignId}</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-[12px] text-white/60 mb-3">
          {c.channel === 'email' ? 'E-mail' : 'SMS'} · {audienceLabel(c.audience, c.sinceDate, c.minPlayHours, c.minRechargeBrl)} · {c.total} destinatário(s)
          {c.channel === 'email' && <> · {c.useTemplate ? 'e-mail padrão' : 'HTML'}</>}
        </p>
        {c.subject && <p className="text-sm text-white font-semibold mb-2">{c.subject}</p>}
        {isHtml ? (
          <iframe title="mensagem" sandbox="" srcDoc={c.body || ''}
            className="w-full h-80 rounded-lg border border-white/[0.06] bg-white" />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-white/80 bg-[#0b1017] border border-white/[0.06] rounded-lg p-3">
            {c.body}
          </pre>
        )}
      </div>
    </div>
  );
}
